/**
 * storage
 *
 * localStorage を直接触るのはこのファイルだけ、というルール。
 * キー名・JSON形式・バリデーションを集約する。
 *
 * 他のコードからは getApiKey() などの関数経由で読み書きする。
 * （CLAUDE.md「localStorageアクセスは必ず lib/storage.ts 経由」）
 *
 * v1ではAPIキーは平文保存（自己責任）。v2でWeb Crypto API化を検討。
 */

import type { ApiKeyData, Mode, ScanResult, UserSettings } from '../types';

/** localStorage のキー名（外部からは触らせない） */
const KEYS = {
  apiKey: 'scouter-lens:api-key',
  /** モード別履歴のキー（v0.3+） */
  historyByMode: {
    translate: 'scouter-lens:history:translate',
    summarize: 'scouter-lens:history:summarize',
    interpret: 'scouter-lens:history:interpret',
  },
  settings: 'scouter-lens:settings',
} as const;

/** v0.2 までの単一履歴キー（マイグレーション元） */
const LEGACY_HISTORY_KEY = 'scouter-lens:history';

/** 履歴の保存上限（要件: 直近10件） */
export const HISTORY_LIMIT = 10;

/** ユーザー設定のデフォルト値（types/index.ts UserSettings 準拠） */
export const DEFAULT_SETTINGS: UserSettings = {
  scanIntervalMs: 5000,
  diffThreshold: 0.08,
};

/** 認識間隔の調整可能範囲（要件 6.4 設定パネル: 2〜10秒） */
export const SCAN_INTERVAL_MIN_MS = 2000;
export const SCAN_INTERVAL_MAX_MS = 10000;

// ============================================================================
//  APIキー
// ============================================================================

/**
 * APIキー文字列のフォーマット簡易チェック
 *
 * Anthropic公式キーは `sk-ant-` プレフィックスから始まる。
 * 完全な厳密照合はサーバー側に任せる（クライアントで弾きすぎない）。
 */
export function isLikelyValidApiKey(raw: string): boolean {
  const trimmed = raw.trim();
  if (trimmed.length < 20) return false;
  if (!trimmed.startsWith('sk-ant-')) return false;
  return true;
}

/**
 * APIキーを localStorage から読む
 * 値が壊れている（JSONパース失敗等）場合は null を返す
 */
export function getApiKey(): string | null {
  try {
    const raw = localStorage.getItem(KEYS.apiKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<ApiKeyData>;
    if (typeof parsed.key !== 'string' || parsed.key.length === 0) {
      return null;
    }
    return parsed.key;
  } catch (err) {
    console.error('[storage] APIキーの読み込みに失敗:', err);
    return null;
  }
}

/**
 * APIキーを localStorage に保存
 */
export function saveApiKey(key: string): void {
  const data: ApiKeyData = {
    key: key.trim(),
    savedAt: new Date().toISOString(),
  };
  localStorage.setItem(KEYS.apiKey, JSON.stringify(data));
}

/**
 * APIキーを削除
 */
export function clearApiKey(): void {
  localStorage.removeItem(KEYS.apiKey);
}

/**
 * ログ出力等で使うマスク表示
 * 例: sk-ant-abcdefghijklmnopqrstuv  →  sk-ant-...****
 */
export function maskApiKey(key: string): string {
  if (key.length <= 10) return '****';
  return `${key.slice(0, 7)}...****`;
}

// ============================================================================
//  履歴
// ============================================================================

/**
 * ScanResult 1件のフィールドが揃っているか確認（壊れデータ防御）
 */
function isScanResult(value: unknown): value is ScanResult {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    (v.mode === 'translate' || v.mode === 'summarize' || v.mode === 'interpret') &&
    typeof v.text === 'string' &&
    typeof v.timestamp === 'string'
  );
}

/**
 * 指定モードの履歴を全件読み出す（新しいものが先頭）
 */
export function getHistory(mode: Mode): ScanResult[] {
  try {
    const raw = localStorage.getItem(KEYS.historyByMode[mode]);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    // 万一他モードのレコードが紛れ込んでいたら除外する
    return parsed
      .filter(isScanResult)
      .filter((r) => r.mode === mode)
      .slice(0, HISTORY_LIMIT);
  } catch (err) {
    console.error('[storage] 履歴の読み込みに失敗:', err);
    return [];
  }
}

/**
 * 指定モードの履歴に1件追加。古いものは溢れて消える（直近HISTORY_LIMIT件まで）。
 * 戻り値は更新後の履歴配列
 */
export function addToHistory(mode: Mode, result: ScanResult): ScanResult[] {
  const current = getHistory(mode);
  // 同じ id が既にある場合は重複保存しない（StrictModeでの二重実行対策）
  if (current.some((r) => r.id === result.id)) {
    return current;
  }
  const next = [result, ...current].slice(0, HISTORY_LIMIT);
  localStorage.setItem(KEYS.historyByMode[mode], JSON.stringify(next));
  return next;
}

/**
 * 指定モードの履歴を全削除
 */
export function clearHistory(mode: Mode): void {
  localStorage.removeItem(KEYS.historyByMode[mode]);
}

/**
 * v0.2 → v0.3 マイグレーション
 * 既存の `scouter-lens:history` 単一キーがあれば、各レコードの mode に従って
 * `scouter-lens:history:<mode>` に振り分けたあと旧キーを削除する。
 *
 * idempotent（旧キーが無くなった以後は no-op）。
 * StrictMode で2回呼ばれても安全。
 */
export function migrateHistoryIfNeeded(): void {
  const legacyRaw = localStorage.getItem(LEGACY_HISTORY_KEY);
  if (!legacyRaw) return;

  try {
    const parsed = JSON.parse(legacyRaw) as unknown;
    if (!Array.isArray(parsed)) {
      localStorage.removeItem(LEGACY_HISTORY_KEY);
      return;
    }

    const items = parsed.filter(isScanResult);
    const byMode: Record<Mode, ScanResult[]> = {
      translate: [],
      summarize: [],
      interpret: [],
    };
    for (const item of items) {
      byMode[item.mode].push(item);
    }

    (Object.keys(byMode) as Mode[]).forEach((mode) => {
      const list = byMode[mode];
      if (list.length === 0) return;
      // 既に新キーにデータがある場合は touch しない（多重マイグレ対策）
      if (localStorage.getItem(KEYS.historyByMode[mode]) !== null) return;
      localStorage.setItem(
        KEYS.historyByMode[mode],
        JSON.stringify(list.slice(0, HISTORY_LIMIT)),
      );
    });

    localStorage.removeItem(LEGACY_HISTORY_KEY);
    console.debug('[storage] migrated legacy history →', items.length, 'items by mode');
  } catch (err) {
    console.warn('[storage] history migration failed, removing legacy key:', err);
    localStorage.removeItem(LEGACY_HISTORY_KEY);
  }
}

// ============================================================================
//  設定
// ============================================================================

/**
 * UserSettings の値が妥当な範囲に収まっているか保証する
 */
function sanitizeSettings(input: Partial<UserSettings>): UserSettings {
  const interval = typeof input.scanIntervalMs === 'number' ? input.scanIntervalMs : DEFAULT_SETTINGS.scanIntervalMs;
  const threshold = typeof input.diffThreshold === 'number' ? input.diffThreshold : DEFAULT_SETTINGS.diffThreshold;
  return {
    scanIntervalMs: Math.min(SCAN_INTERVAL_MAX_MS, Math.max(SCAN_INTERVAL_MIN_MS, interval)),
    diffThreshold: Math.min(1, Math.max(0, threshold)),
  };
}

/**
 * 設定を読み出す。未保存なら DEFAULT_SETTINGS を返す。
 */
export function getSettings(): UserSettings {
  try {
    const raw = localStorage.getItem(KEYS.settings);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<UserSettings>;
    return sanitizeSettings(parsed);
  } catch (err) {
    console.error('[storage] 設定の読み込みに失敗:', err);
    return DEFAULT_SETTINGS;
  }
}

/**
 * 設定を保存
 */
export function saveSettings(settings: UserSettings): void {
  localStorage.setItem(KEYS.settings, JSON.stringify(sanitizeSettings(settings)));
}
